import { Box, chakra, Text } from '@chakra-ui/react';
import type { PortableTextBlock } from '@portabletext/types';
import { Badge, TextExt } from '@time-line/components/common';
import { VerifiedTick } from '@time-line/components/icons';
import { CustomerResults } from 'components/common/CustomerResults';
import {
  ArticleContainer,
  RichTextResolver,
} from 'components/common/RichTextResolver';
import { ProductReviewsDrawer } from 'components/reviews/ProductReviewsDrawer';
import type { ProductBullet } from 'types/cms/ProductBullet';

import { ProductBadges } from '../ProductBadges';

interface ProductIntroProps {
  title: string;
  subtitle?: string;
  longDescription: PortableTextBlock[] | string;
  productBullet?: ProductBullet;
  secondaryProductBullet?: ProductBullet;
  isPreorder?: boolean;
  showReviews?: boolean;
  productSlugs: string[];
  netContents?: string;
  isMoneyBackGuarantee?: boolean;
  showCustomerResults?: boolean;
}

export function ProductIntro({
  title,
  subtitle,
  longDescription,
  productBullet,
  secondaryProductBullet,
  isPreorder,
  showReviews,
  productSlugs,
  netContents,
  isMoneyBackGuarantee,
  showCustomerResults,
}: ProductIntroProps) {
  return (
    <Box className="product_intro" display="block">
      <ProductBadges
        productBullet={productBullet}
        secondaryProductBullet={secondaryProductBullet}
        isPreorder={isPreorder}
        mb="xs"
      />
      {showCustomerResults && <CustomerResults mb="2xs" />}
      <TextExt as="h1" size={title.length > 20 ? 'h3' : 'h2'} mb="2xs">
        {title}
        {subtitle && (
          <chakra.span display="block" color="blackAlpha.500">
            {subtitle}
          </chakra.span>
        )}
      </TextExt>

      {showReviews && (
        <Box display="block" mb="2xs">
          <ProductReviewsDrawer
            productSlugs={productSlugs}
            productTitle={title}
          />
        </Box>
      )}

      {isMoneyBackGuarantee && (
        <Box mt="sm" mb="2xs" display="block">
          <Badge
            colorScheme="lightgray"
            leftIcon={<VerifiedTick width="16px" height="16px" />}
            leftIconWrapperProps={{ mr: '5xs', ml: '2px' }}
          >
            30 day money-back guarantee
          </Badge>
        </Box>
      )}

      {longDescription && typeof longDescription === 'string' && (
        <Text size="body-serif">{longDescription}</Text>
      )}
      {longDescription && typeof longDescription !== 'string' && (
        <RichTextResolver
          blocks={longDescription}
          container={ArticleContainer}
        />
      )}
      {netContents && (
        <Text
          size="detail"
          fontWeight="book"
          color="blackAlpha.600"
          mt="3xs"
          sx={{
            fontVariantNumeric: 'slashed-zero',
          }}
        >
          {netContents}
        </Text>
      )}
    </Box>
  );
}
